import { useSyncExternalStore } from 'react'

export type ThemePreference = 'system' | 'light' | 'dark'

/** What each preference is called wherever it is offered: the account menu, the More screen. */
export const THEME_LABELS: Record<ThemePreference, string> = {
  system: 'System',
  light: 'Light',
  dark: 'Dark',
}

const STORAGE_KEY = 'dealerdrive.theme'
const listeners = new Set<() => void>()

function read(): ThemePreference {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return value === 'light' || value === 'dark' ? value : 'system'
  } catch {
    return 'system'
  }
}

let preference: ThemePreference = read()

function systemQuery(): MediaQueryList | null {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null
}

export function resolveTheme(value: ThemePreference = preference): 'light' | 'dark' {
  if (value !== 'system') return value
  return systemQuery()?.matches ? 'dark' : 'light'
}

/** The `.dark` class on <html> is what the Tailwind `dark:` variant and the theme tokens key off. */
function apply() {
  document.documentElement.classList.toggle('dark', resolveTheme() === 'dark')
}

export const themeStore = {
  get(): ThemePreference {
    return preference
  },
  set(next: ThemePreference) {
    preference = next
    try {
      if (next === 'system') localStorage.removeItem(STORAGE_KEY)
      else localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* storage unavailable (private mode) */
    }
    apply()
    for (const listener of listeners) listener()
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
}

/** Call once at startup. `index.html` applies the class inline too, so there is no flash on load. */
export function initTheme() {
  apply()
  systemQuery()?.addEventListener('change', () => {
    if (preference === 'system') apply()
  })
}

export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(themeStore.subscribe, themeStore.get)
}

/** Both things that can change the resolved theme: the stored preference and the OS scheme. */
function subscribeToResolved(listener: () => void): () => void {
  const unsubscribe = themeStore.subscribe(listener)
  const query = systemQuery()
  query?.addEventListener('change', listener)
  return () => {
    unsubscribe()
    query?.removeEventListener('change', listener)
  }
}

/** The theme actually on screen, for components that cannot read the `.dark` class themselves. */
export function useResolvedTheme(): 'light' | 'dark' {
  return useSyncExternalStore(subscribeToResolved, () => resolveTheme())
}
